import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AccessibilityContext } from './AccessibilityContext';

import {
  DEFAULT_ACCESSIBILITY_STATE,
  FONT_STEP,
  MAX_FONT_SCALE,
  MIN_FONT_SCALE,
} from '../utils/accessibility.constants';

import { AccessibilityState, ThemeMode } from '../types/accessibility.types';

import { applyAccessibilitySettings } from '../utils/accessibility.helpers';

import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

export const AccessibilityProvider = ({
  children,
}: PropsWithChildren) => {
  const [state, setState] = useState<AccessibilityState>(() => {
    const saved = localStorage.getItem('app_accessibility_settings');

    if (!saved) return DEFAULT_ACCESSIBILITY_STATE;

    try {
      return JSON.parse(saved);
    } catch {
      return DEFAULT_ACCESSIBILITY_STATE;
    }
  });

  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    localStorage.setItem(
      'app_accessibility_settings',
      JSON.stringify(state)
    );

    applyAccessibilitySettings(state);
  }, [state]);

  const announce = useCallback((message: string) => {
    setAnnouncement('');

    setTimeout(() => {
      setAnnouncement(message);
    }, 100);
  }, []);

  const increaseFont = useCallback(() => {
    setState((prev) => ({
      ...prev,
      fontScale: Math.min(prev.fontScale + FONT_STEP, MAX_FONT_SCALE),
    }));

    announce('Font size increased');
  }, [announce]);

  const decreaseFont = useCallback(() => {
    setState((prev) => ({
      ...prev,
      fontScale: Math.max(prev.fontScale - FONT_STEP, MIN_FONT_SCALE),
    }));

    announce('Font size decreased');
  }, [announce]);

  const resetFont = useCallback(() => {
    setState((prev) => ({
      ...prev,
      fontScale: 1,
    }));

    announce('Font size reset');
  }, [announce]);

  const setTheme = useCallback(
    (theme: ThemeMode) => {
      setState((prev) => ({
        ...prev,
        theme,
      }));

      announce(`${theme} theme enabled`);
    },
    [announce]
  );

  const toggleSetting = useCallback(
    (key: keyof AccessibilityState, label: string) => {
      setState((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));

      announce(label);
    },
    [announce]
  );

  const resetAll = useCallback(() => {
    setState(DEFAULT_ACCESSIBILITY_STATE);

    announce('Accessibility settings reset');
  }, [announce]);

  useKeyboardShortcuts({
    increaseFont,
    decreaseFont,
    resetFont,
  });

  const value = useMemo(
    () => ({
      state,

      increaseFont,
      decreaseFont,
      resetFont,

      setTheme,

      toggleUnderlineLinks: () =>
        toggleSetting('underlineLinks', 'Underline links toggled'),

      toggleHighlightLinks: () =>
        toggleSetting('highlightLinks', 'Highlight links toggled'),

      toggleReadableFont: () =>
        toggleSetting('readableFont', 'Readable font toggled'),

      toggleReduceMotion: () =>
        toggleSetting('reduceMotion', 'Reduce motion toggled'),

      toggleTextSpacing: () =>
        toggleSetting('textSpacing', 'Text spacing toggled'),

      toggleLargeCursor: () =>
        toggleSetting('largeCursor', 'Large cursor toggled'),

      toggleReadingGuide: () =>
        toggleSetting('readingGuide', 'Reading guide toggled'),

      resetAll,

      announce,
    }),
    [
      state,
      increaseFont,
      decreaseFont,
      resetFont,
      setTheme,
      toggleSetting,
      resetAll,
      announce,
    ]
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}

      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
    </AccessibilityContext.Provider>
  );
};